import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Grid,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Button,
  Alert,
  CircularProgress,
  Typography,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  TableContainer,
  TablePagination,
  Paper,
  Chip,
  IconButton,
  Collapse,
  Tooltip,
} from '@mui/material';
import {
  Download as DownloadIcon,
  Refresh as RefreshIcon,
  KeyboardArrowDown as ExpandIcon,
  KeyboardArrowUp as CollapseIcon,
  FilterAltOff as ClearIcon,
} from '@mui/icons-material';
import { apiService } from '../../services/apiService';
import { usePermission } from '../../hooks/usePermission';

const RESOURCES = ['detections', 'cases', 'indicators', 'entities', 'users', 'roles', 'agents', 'settings', 'invitations', 'api-keys'];

const DEFAULT_FILTERS = { resource: '', success: '', from: '', to: '', search: '' };

const serverMessage = (err, fallback) => err?.response?.data?.message || err?.message || fallback;

const methodColor = (m) => ({ POST: 'success', PUT: 'info', PATCH: 'info', DELETE: 'error' }[m] || 'default');

const formatDate = (v) => {
  if (!v) return '';
  const d = new Date(v);
  return Number.isNaN(d.getTime()) ? String(v) : d.toLocaleString();
};

const LogRow = ({ log }) => {
  const [open, setOpen] = useState(false);
  const hasDetails = log.details && Object.keys(log.details).length > 0;
  return (
    <>
      <TableRow hover sx={{ '& > *': { borderBottom: open ? 'unset' : undefined } }}>
        <TableCell padding="checkbox">
          <IconButton size="small" onClick={() => setOpen((o) => !o)} aria-label="expand row">
            {open ? <CollapseIcon /> : <ExpandIcon />}
          </IconButton>
        </TableCell>
        <TableCell sx={{ whiteSpace: 'nowrap' }}>{formatDate(log.createdAt)}</TableCell>
        <TableCell>
          <Typography variant="body2">{log.userName || '—'}</Typography>
          <Typography variant="caption" color="text.secondary">{log.userEmail}</Typography>
        </TableCell>
        <TableCell>
          <Chip label={log.method} size="small" color={methodColor(log.method)} variant="outlined" />
        </TableCell>
        <TableCell>
          <Typography variant="body2" sx={{ fontFamily: 'monospace', fontSize: 12 }}>{log.path}</Typography>
        </TableCell>
        <TableCell>{log.resource}</TableCell>
        <TableCell>{log.action}</TableCell>
        <TableCell>
          <Chip label={log.status} size="small" color={log.success ? 'success' : 'error'} />
        </TableCell>
        <TableCell sx={{ whiteSpace: 'nowrap' }}>{log.ip || ''}</TableCell>
      </TableRow>
      <TableRow>
        <TableCell colSpan={9} sx={{ py: 0, bgcolor: 'action.hover' }}>
          <Collapse in={open} timeout="auto" unmountOnExit>
            <Box sx={{ p: 2 }}>
              <Grid container spacing={2}>
                <Grid item xs={12} md={4}>
                  <Typography variant="caption" color="text.secondary">Resource ID</Typography>
                  <Typography variant="body2" sx={{ fontFamily: 'monospace', wordBreak: 'break-all' }}>{log.resourceId || '—'}</Typography>
                  <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>Log ID</Typography>
                  <Typography variant="body2" sx={{ fontFamily: 'monospace', wordBreak: 'break-all' }}>{log._id}</Typography>
                  {log.userAgent && (
                    <>
                      <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>User agent</Typography>
                      <Typography variant="body2" sx={{ wordBreak: 'break-all' }}>{log.userAgent}</Typography>
                    </>
                  )}
                </Grid>
                <Grid item xs={12} md={8}>
                  <Typography variant="caption" color="text.secondary">Details</Typography>
                  <Box component="pre" sx={{ m: 0, p: 1.5, bgcolor: 'background.paper', border: 1, borderColor: 'divider', borderRadius: 1, fontSize: 12, overflowX: 'auto', maxHeight: 300 }}>
                    {hasDetails ? JSON.stringify(log.details, null, 2) : 'No additional details recorded.'}
                  </Box>
                </Grid>
              </Grid>
            </Box>
          </Collapse>
        </TableCell>
      </TableRow>
    </>
  );
};

const AuditLog = () => {
  const { canManageCompany } = usePermission();
  const [filters, setFilters] = useState(DEFAULT_FILTERS);
  const [applied, setApplied] = useState(DEFAULT_FILTERS);
  const [page, setPage] = useState(0);
  const [limit, setLimit] = useState(50);
  const [logs, setLogs] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [error, setError] = useState(null);

  const load = useCallback(async () => {
    if (!canManageCompany) return;
    setLoading(true);
    setError(null);
    try {
      const params = { page: page + 1, limit };
      if (applied.resource) params.resource = applied.resource;
      if (applied.success !== '') params.success = applied.success;
      if (applied.from) params.from = new Date(applied.from).toISOString();
      if (applied.to) {
        const to = new Date(applied.to);
        to.setHours(23, 59, 59, 999);
        params.to = to.toISOString();
      }
      if (applied.search.trim()) params.search = applied.search.trim();
      const res = await apiService.audit.getAll(params);
      setLogs(res.data.logs || []);
      setTotal(res.data.pagination?.total || 0);
    } catch (e) {
      setError(serverMessage(e, 'Failed to load audit log'));
    } finally {
      setLoading(false);
    }
  }, [canManageCompany, page, limit, applied]);

  useEffect(() => { load(); }, [load]);

  const applyFilters = () => { setPage(0); setApplied(filters); };
  const clearFilters = () => { setFilters(DEFAULT_FILTERS); setApplied(DEFAULT_FILTERS); setPage(0); };

  const handleExport = async () => {
    setExporting(true);
    setError(null);
    try {
      await apiService.download('/audit/export', 'audit-log.csv');
    } catch (e) {
      setError(serverMessage(e, 'Failed to export audit log'));
    } finally {
      setExporting(false);
    }
  };

  if (!canManageCompany) {
    return <Alert severity="warning">You need the company:manage permission to view the audit log.</Alert>;
  }

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2, flexWrap: 'wrap', gap: 1 }}>
        <Box>
          <Typography variant="h6">Audit log</Typography>
          <Typography variant="body2" color="text.secondary">Every change made through the API, who made it and whether it succeeded.</Typography>
        </Box>
        <Box sx={{ display: 'flex', gap: 1 }}>
          <Tooltip title="Refresh"><IconButton onClick={load} disabled={loading}><RefreshIcon /></IconButton></Tooltip>
          <Button variant="outlined" startIcon={exporting ? <CircularProgress size={16} /> : <DownloadIcon />} onClick={handleExport} disabled={exporting}>
            Export CSV
          </Button>
        </Box>
      </Box>

      {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>{error}</Alert>}

      <Paper variant="outlined" sx={{ p: 2, mb: 2 }}>
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} sm={6} md={2}>
            <FormControl fullWidth size="small">
              <InputLabel>Resource</InputLabel>
              <Select label="Resource" value={filters.resource} onChange={(e) => setFilters((f) => ({ ...f, resource: e.target.value }))}>
                <MenuItem value="">All</MenuItem>
                {RESOURCES.map((r) => <MenuItem key={r} value={r}>{r}</MenuItem>)}
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} sm={6} md={2}>
            <FormControl fullWidth size="small">
              <InputLabel>Result</InputLabel>
              <Select label="Result" value={filters.success} onChange={(e) => setFilters((f) => ({ ...f, success: e.target.value }))}>
                <MenuItem value="">All</MenuItem>
                <MenuItem value="true">Success</MenuItem>
                <MenuItem value="false">Failed</MenuItem>
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} sm={6} md={2}>
            <TextField fullWidth size="small" type="date" label="From" InputLabelProps={{ shrink: true }} value={filters.from} onChange={(e) => setFilters((f) => ({ ...f, from: e.target.value }))} />
          </Grid>
          <Grid item xs={12} sm={6} md={2}>
            <TextField fullWidth size="small" type="date" label="To" InputLabelProps={{ shrink: true }} value={filters.to} onChange={(e) => setFilters((f) => ({ ...f, to: e.target.value }))} />
          </Grid>
          <Grid item xs={12} sm={8} md={3}>
            <TextField
              fullWidth size="small" label="Search" placeholder="Path, user, email or resource ID"
              value={filters.search}
              onChange={(e) => setFilters((f) => ({ ...f, search: e.target.value }))}
              onKeyDown={(e) => { if (e.key === 'Enter') applyFilters(); }}
            />
          </Grid>
          <Grid item xs={12} sm={4} md={1} sx={{ display: 'flex', gap: 1 }}>
            <Button variant="contained" size="small" onClick={applyFilters} fullWidth>Apply</Button>
            <Tooltip title="Clear filters"><IconButton size="small" onClick={clearFilters}><ClearIcon /></IconButton></Tooltip>
          </Grid>
        </Grid>
      </Paper>

      <TableContainer component={Paper} variant="outlined">
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell padding="checkbox" />
              <TableCell>Time</TableCell>
              <TableCell>User</TableCell>
              <TableCell>Method</TableCell>
              <TableCell>Path</TableCell>
              <TableCell>Resource</TableCell>
              <TableCell>Action</TableCell>
              <TableCell>Status</TableCell>
              <TableCell>IP</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {loading ? (
              <TableRow><TableCell colSpan={9} align="center" sx={{ py: 4 }}><CircularProgress size={28} /></TableCell></TableRow>
            ) : logs.length === 0 ? (
              <TableRow><TableCell colSpan={9} align="center" sx={{ py: 4 }}><Typography color="text.secondary">No audit entries match these filters.</Typography></TableCell></TableRow>
            ) : (
              logs.map((log) => <LogRow key={log._id} log={log} />)
            )}
          </TableBody>
        </Table>
        <TablePagination
          component="div"
          count={total}
          page={page}
          onPageChange={(e, p) => setPage(p)}
          rowsPerPage={limit}
          onRowsPerPageChange={(e) => { setLimit(parseInt(e.target.value, 10)); setPage(0); }}
          rowsPerPageOptions={[25, 50, 100, 200]}
        />
      </TableContainer>
    </Box>
  );
};

export default AuditLog;
